import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { getLocalStorage, setLocalStorage } from '@/utils/storages';
import { SERVICES } from '@/configs';
import { useEffect } from 'react';

type LoginParams = {
  username: string;
  password: string;
};

async function fetchAccessToken(loginParams: LoginParams): Promise<string> {
  const token = getLocalStorage('access_token');
  if (token) return token;

  if (loginParams.username === '' || loginParams.password === '') {
    throw new Error('Username or password is empty');
  }
  
  if (
    !process.env.REACT_APP_BASIC_UNAME ||
    !process.env.REACT_APP_BASIC_PASSWORD
  ) {
    throw new Error('Basic auth credentials are not set in environment variables');
  }

  const options = {
    auth: {
      username: process.env.REACT_APP_BASIC_UNAME as string,
      password: process.env.REACT_APP_BASIC_PASSWORD as string,
    },
    method: 'POST',
    url: SERVICES.LOGIN,
    headers: {
      'Content-Type': 'application/json',
    },
    data: {
      username: loginParams.username,
      password: CryptoJS.MD5(loginParams.password).toString(),
    },
  };

  const res = await axios(options);

  if (res?.data?.code === 200) {
    setLocalStorage('access_token', res?.data?.data?.userToken);
    setLocalStorage('user_data', JSON.stringify(res?.data?.data));

    return res?.data?.data;
  }
  throw new Error('No access token in response');
}

export function useAuth(loginParams: LoginParams) {
  const queryClient = useQueryClient();
  const queryKey = ['access_token'];

  const query = useQuery({
    queryKey: ['access_token'],
    queryFn: () => fetchAccessToken(loginParams),
    enabled: false,
    retry: 1,
  });

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey });
    };
  }, [queryClient]);

  const { data, error, isLoading, isError, refetch } = query;

  return { data, error, isLoading, isError, refetch };
}
